// 🔧 email_templates.js
// ✅ Purpose: Canonical registry of all outbound LGV email templates
// ✅ Features:
// - Defines every supported outbound email template
// - Supplies Communication Studio metadata
// - Defines editable fields
// - Defines supported merge variables
// - Provides starter content
// - Future support for localization, AI generation, previews,
//   white-label branding, accessibility validation, and template versioning.

import { CONTENT_TYPES } from "./content_types";

export const EMAIL_TEMPLATE_FIELDS = [
    {
        element: "subject",
        label: "Email Subject",
        type: CONTENT_TYPES.EMAIL_SUBJECT,
    },
    {
        element: "heading",
        label: "Heading",
        type: CONTENT_TYPES.EMAIL_HEADING,
    },
    {
        element: "html",
        label: "HTML Body",
        type: CONTENT_TYPES.EMAIL_HTML,
    },
    {
        element: "text",
        label: "Plain Text Body",
        type: CONTENT_TYPES.EMAIL_BODY,
    },
    {
        element: "button_label",
        label: "Button Label",
        type: CONTENT_TYPES.EMAIL_BUTTON_LABEL,
    },
    {
        element: "footer",
        label: "Footer",
        type: CONTENT_TYPES.EMAIL_FOOTER,
    },
];

export const EMAIL_TEMPLATES = [
    {
        id: "auction_pickup",

        label: "Auction Pickup Reminder",

        description:
            "Sent to winning bidder when pickup instructions are available.",

        fields: EMAIL_TEMPLATE_FIELDS,

        variables: [
            "recipientName",
            "eventName",
            "itemTitle",
            "pickupLocation",
            "pickupTime",
            "pickupInstructions",
            "buttonLabel",
        ],

        defaults: {

            subject:
                "Auction Pickup Information - {itemTitle}",

            heading:
                "Auction Pickup",

            html:
                "<p>Your auction item is ready for pickup.</p>",

            text:
                "Your auction item is ready for pickup.",

            button_label:
                "View Pickup Information",

            footer:
                "— The Let's Go Vote Team",

        },

    },
    {
        id: "event_manager",

        label: "Event Manager",

        description:
            "Notification sent when a user becomes an Event Manager.",

        fields:
            EMAIL_TEMPLATE_FIELDS,

        variables: [
            "recipientName",
            "eventName",
            "eventUrl",
            "buttonLabel",
        ],

        defaults: {

            subject:
                "📣 New Event Manager Assignment - {eventName}",

            heading:
                "Event Manager Assignment",

            html:
                "<p>You have been assigned as an Event Manager.</p>",

            text:
                "You have been assigned as an Event Manager.",

            button_label:
                "Manage Event",

            footer:
                "— The Let's Go Vote Team",

        },

    },
    {
        id: "event_request",
        fields: EMAIL_TEMPLATE_FIELDS,
        label: "Event Request",

        description:
            "Internal notification when a public event request is submitted.",

        variables: [
            "recipientName",
            "eventName",
            "contactEmail",
            "organizationName",
            "buttonUrl",
        ],

        defaults: {
            subject:
                "🚀 New Event Request: {eventName}",

            heading:
                "New Event Request",

            html:
                "<p>A new event request has been submitted.</p>",

            text:
                "A new event request has been submitted.",

            button_label:
                "Review Request",

            footer:
                "Generated automatically by Let's Go Vote.",
        },
    },

    {
        id: "event_approval",
        fields: EMAIL_TEMPLATE_FIELDS,
        label: "Event Approval",

        description:
            "Notification sent after an event has been approved.",

        variables: [
            "recipientName",
            "eventName",
            "eventUrl",
        ],

        defaults: {
            subject:
                "✅ Your event is ready: {eventName}",

            heading:
                "Your Event Is Ready",

            html:
                "<p>Your event is now live.</p>",

            text:
                "Your event is now live.",

            button_label:
                "Open Event",

            footer:
                "Generated automatically by Let's Go Vote.",
        },
    },

    {
        id: "user_invite",
        fields: EMAIL_TEMPLATE_FIELDS,
        label: "User Invitation",

        description:
            "Invitation for managers, voters and future invitees.",

        variables: [
            "recipientName",
            "eventName",
            "inviteUrl",
            "role",
        ],

        defaults: {
            subject:
                "📨 Invitation to {eventName}",

            heading:
                "You're Invited",

            html:
                "<p>You've been invited.</p>",

            text:
                "You've been invited.",

            button_label:
                "Complete Registration",

            footer:
                "Generated automatically by Let's Go Vote.",
        },
    },

    {
        id: "sponsor_contact",
        fields: EMAIL_TEMPLATE_FIELDS,
        label: "Sponsor Contact",

        description:
            "Sponsor point-of-contact notification.",

        variables: [
            "recipientName",
            "eventName",
            "sponsorName",
            "sponsorUrl",
        ],

        defaults: {
            subject:
                "🌟 Sponsor Contact",

            heading:
                "Sponsor Contact",

            html:
                "<p>You have been added as a sponsor contact.</p>",

            text:
                "You have been added as a sponsor contact.",

            button_label:
                "Review Sponsor",

            footer:
                "Generated automatically by Let's Go Vote.",
        },
    },

    {
        id: "volunteer_invite",
        fields: EMAIL_TEMPLATE_FIELDS,
        label: "Volunteer Invitation",

        description:
            "Volunteer invitation email.",

        variables: [
            "recipientName",
            "activityTitle",
            "shiftTime",
        ],

        defaults: {
            subject:
                "Volunteer Invitation",

            heading:
                "Volunteer Invitation",

            html:
                "<p>Thank you for volunteering.</p>",

            text:
                "Thank you for volunteering.",

            button_label:
                "View Assignment",

            footer:
                "Generated automatically by Let's Go Vote.",
        },
    },

    {
        id: "volunteer_signup",
        fields: EMAIL_TEMPLATE_FIELDS,
        label: "Volunteer Signup",

        description:
            "Volunteer signup confirmation.",

        variables: [
            "recipientName",
            "activityTitle",
            "shiftTime",
        ],

        defaults: {
            subject:
                "Volunteer Signup",

            heading:
                "Volunteer Signup",

            html:
                "<p>Your volunteer signup is confirmed.</p>",

            text:
                "Your volunteer signup is confirmed.",

            button_label:
                "View Assignment",

            footer:
                "Generated automatically by Let's Go Vote.",
        },
    },

    {
        id: "auction_outbid",
        fields: EMAIL_TEMPLATE_FIELDS,
        label: "Auction Outbid",

        description:
            "Auction outbid notification.",

        variables: [
            "recipientName",
            "itemTitle",
            "currentBid",
            "auctionUrl",
        ],

        defaults: {
            subject:
                "You've Been Outbid",

            heading:
                "You've Been Outbid",

            html:
                "<p>You have been outbid.</p>",

            text:
                "You have been outbid.",

            button_label:
                "Place Another Bid",

            footer:
                "Generated automatically by Let's Go Vote.",
        },
    },

];

export function getEmailTemplate(templateId) {
    return EMAIL_TEMPLATES.find(
        (template) => template.id === templateId
    );
}

export function getEmailTemplateIds() {
    return EMAIL_TEMPLATES.map(
        (template) => template.id
    );
}

/**
 * Builds starter Communication Engine items for one email template.
 *
 * Used by Communication Studio to generate editable content rows.
 */
export function buildEmailStarterItems(template) {

    return template.fields.map((field) => ({

        section: "email",

        emailTemplate: template.id,

        element: field.element,

        label: field.label,

        type: field.type,

        variables: template.variables,

        defaultValue:
            template.defaults[field.element] ?? "",

    }));

}